// adapters/relay.ts
// Relay API Adapter — streams from the FastAPI + SSE relay service.
// NOTE: only streaming (plus demo-bot provisioning) lives here; everything else
// is owned by the Supabase adapter.
//
// All streaming endpoints share ONE SSE reader (`runSSEStream`). Endpoint
// functions are thin configurations of it: a body, a log label, and an event
// dispatch table. Per-endpoint differences that matter are explicit config
// (notably `terminalError` — a conversation stream ends on `error`, while a
// creator stream historically keeps reading).

import { supabase } from '../../lib/supabaseClient';
import { recordPromptDispatch } from '@/dev/creatorPromptInspector';
import type {
  ContinuationDoneEnvelope,
  CreatorToolCallStreamEvent,
  GenerateStreamCallbacks,
  GenerateStreamRequest,
} from '../contract';
import {
  ContinuationProtocolError,
  ContinuationStreamError,
  continuationErrorFromPayload,
  validateContinuationDoneEnvelope,
} from '@/api/continuationProtocol';

// Endpoint config:
// - Use a full endpoint like "http://localhost:8000"
// - or keep default "/relay-api" if you proxy in Vite.
const RELAY_BASE_URL: string =
  (import.meta.env && import.meta.env.VITE_API_STREAM_URL);

interface SSEEvent {
  event: string;
  data: string;
}

interface SSECarry {
  buffer: string;
}

interface StreamMeta {
  stream_id?: string;
  message_id?: number;
  alternative_id?: number;
  [key: string]: unknown;
}

interface RelayError extends Error {
  status?: number;
}

type ChunkCallback = (chunk: string) => Promise<void> | void;
type ErrorCallback = (error: Error) => Promise<void> | void;
type CompleteCallback = (
  aborted: boolean,
  meta?: StreamMeta | ContinuationDoneEnvelope,
) => Promise<void> | void;
type ConnectedCallback = () => void;
type CreatorToolCallCallback = (event: CreatorToolCallStreamEvent) => Promise<void> | void;

/** Defensive check for a real AbortSignal */
function isAbortSignal(s: unknown): s is AbortSignal {
  return !!s && typeof s === 'object' && 'aborted' in s && typeof (s as AbortSignal).addEventListener === 'function';
}


/** Robust SSE block parser (handles event:, multi-line data:, comments, CRLFs) */
function* parseSSE(textChunk: string, carry: SSECarry): Generator<SSEEvent> {
  carry.buffer += textChunk;
  const blocks = carry.buffer.split(/\r?\n\r?\n/);
  // Keep last (possibly partial) block in carry
  carry.buffer = blocks.pop() ?? '';

  for (const block of blocks) {
    let event = 'message';
    const dataLines: string[] = [];
    const lines = block.split(/\r?\n/);

    for (const line of lines) {
      if (!line) continue;
      if (line.startsWith(':')) continue; // SSE comment
      if (line.startsWith('event:')) {
        event = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
       // Preserve token whitespace. Per SSE spec, ignore at most one leading space after "data:".
       const raw = line.slice(5);
       const datum = raw.startsWith(' ') ? raw.slice(1) : raw;
       dataLines.push(datum);
      }
      // Ignore other fields (id:, retry:, etc.) for now
    }
    yield { event, data: dataLines.join('\n') };
  }
}

/** Resolve the Supabase JWT the relay authenticates with. */
async function getRelayAuthToken(onError?: ErrorCallback): Promise<string> {
  const { data, error: authErr } = await supabase.auth.getSession();
  if (authErr) {
    const err = new Error(`[RelayAdapter] supabase.auth.getSession failed: ${authErr.message}`);
    onError?.(err);
    throw err;
  }
  const jwt = data?.session?.access_token;
  if (!jwt) {
    const err = new Error('[RelayAdapter] No active session (missing JWT).');
    onError?.(err);
    throw err;
  }
  return jwt;
}

/** Build the error a non-2xx relay response should surface. */
async function buildHttpError(res: Response): Promise<Error> {
  const text = await res.text().catch(() => '');
  let msg = `[RelayAdapter] HTTP ${res.status}`;
  try {
    const maybe = JSON.parse(text);
    msg = maybe?.error || msg;
  } catch { /* not JSON */ }
  return new Error(msg);
}

/** Whether an SSE event handler wants the stream to keep reading or stop. */
type SSEEventOutcome = 'continue' | 'terminate';

type SSEEventHandler = (data: string) => Promise<SSEEventOutcome | void> | SSEEventOutcome | void;

interface SSEStreamConfig {
  /** Path under the relay base URL, e.g. `/v1/stream`. */
  path: string;
  /** Request body, serialized as JSON. */
  body: unknown;
  /** Calling method name; used to attribute every diagnostic log. */
  label: string;
  abortSignal: AbortSignal | unknown;
  /** Mirror the request into the dev prompt inspector. */
  recordDispatch?: boolean;
  /** Event name → handler. Handlers returning 'terminate' end the stream. */
  events: Record<string, SSEEventHandler>;
  onConnected?: ConnectedCallback;
  onError?: ErrorCallback;
  onComplete?: CompleteCallback;
}

/**
 * The single SSE reader behind every relay streaming endpoint: auth, POST,
 * response validation, chunked read, parse, and dispatch. Returns an abort
 * handle when this call owns the AbortController, matching the historical
 * per-endpoint return contract.
 */
async function runSSEStream({
  path,
  body,
  label,
  abortSignal,
  recordDispatch = false,
  events,
  onConnected,
  onError,
  onComplete,
}: SSEStreamConfig): Promise<{ abort: () => void } | null> {
  const jwt = await getRelayAuthToken(onError);

  let controller: AbortController | null = null;
  let signal: AbortSignal | undefined = undefined;
  if (isAbortSignal(abortSignal)) {
    signal = abortSignal;
  } else if (abortSignal != null) {
    console.warn(`[RelayAdapter] ${label} received a non-AbortSignal abortSignal. Ignoring.`);
  }
  if (!signal && typeof AbortController !== 'undefined') {
    controller = new AbortController();
    signal = controller.signal;
  }
  const abortHandle = () => (controller ? { abort: () => controller.abort() } : null);

  try {
    const endpoint = `${RELAY_BASE_URL}${path}`;

    if (recordDispatch) recordPromptDispatch(body as Record<string, unknown>, endpoint);

    const res = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${jwt}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
      credentials: 'include',
      ...(signal ? { signal } : {}),
    });

    if (!res.ok) {
      const err = await buildHttpError(res);
      onError?.(err);
      throw err;
    }

    onConnected?.();

    if (!res.body) {
      const err = new Error('[RelayAdapter] ReadableStream not supported in this environment.');
      onError?.(err);
      throw err;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    const carry: SSECarry = { buffer: '' };

    try {
      while (true) {
        if (signal?.aborted) {
          await onComplete?.(true);
          break;
        }

        const { done, value } = await reader.read();
        if (done) {
          // If the server forgot to send "done", still finalize cleanly.
          await onComplete?.(false);
          break;
        }

        const chunk = decoder.decode(value, { stream: true });

        for (const { event, data } of parseSSE(chunk, carry)) {
          // Comments, empty blocks, and pings carry no payload for callers.
          if (event === 'ping') continue;

          const handler = events[event];
          if (!handler) continue;

          const outcome = await handler(data);
          if (outcome === 'terminate') return abortHandle();
        }
      }
    } finally {
      reader.releaseLock();
    }
  } catch (error) {
    if ((error as Error)?.name !== 'AbortError') {
      console.error(`[RelayAdapter] ${label} request failed:`, error);
      onError?.(error as Error);
      throw error;
    } else {
      // graceful abort
      await onComplete?.(true);
    }
  }

  return abortHandle();
}

const asContinuationError = (error: unknown): ContinuationStreamError => {
  if (error instanceof ContinuationStreamError) return error;
  const message = error instanceof Error ? error.message : String(error);
  return new ContinuationStreamError(message || '[RelayAdapter] Continuation stream failed.')
}

async function buildContinuationHttpError(res: Response): Promise<ContinuationStreamError> {
  const text = await res.text().catch(() => '')
  let message = `[RelayAdapter] HTTP ${res.status}`
  try {
    const payload = JSON.parse(text)
    if (typeof payload?.error === 'string' && payload.error.length > 0) message = payload.error
  } catch { /* non-JSON response */ }
  const error = new ContinuationStreamError(message)
  error.status = res.status
  return error
}

/** Strict reader used only by continuation_v1; legacy stream behavior is unchanged. */
async function runContinuationStream({
  body,
  expectedStreamId,
  callbacks,
}: {
  body: Record<string, unknown>
  expectedStreamId: string
  callbacks: GenerateStreamCallbacks
}): Promise<{ abort: () => void } | null> {
  const {
    abortSignal,
    onChunk,
    onReasoningChunk,
    onToolCallStart,
    onConnected,
    onComplete,
    onError,
  } = callbacks

  let controller: AbortController | null = null
  let signal: AbortSignal | undefined
  if (isAbortSignal(abortSignal)) {
    signal = abortSignal
  } else if (abortSignal != null) {
    console.warn('[RelayAdapter] generateStream received a non-AbortSignal abortSignal. Ignoring.')
  }
  if (!signal && typeof AbortController !== 'undefined') {
    controller = new AbortController()
    signal = controller.signal
  }

  let terminal = false
  let reader: ReadableStreamDefaultReader<Uint8Array> | null = null
  let reachedEof = false

  const abortHandle = () => (controller ? { abort: () => controller?.abort() } : null)
  const finishAbort = async (): Promise<void> => {
    if (terminal) return
    terminal = true
    await onComplete?.(true)
  }
  const finishError = async (error: unknown): Promise<never> => {
    const continuationError = asContinuationError(error)
    if (!terminal) {
      terminal = true
      await onError?.(continuationError)
    }
    throw continuationError
  }

  try {
    if (signal?.aborted) {
      await finishAbort()
      return abortHandle()
    }

    const jwt = await getRelayAuthToken()
    const endpoint = `${RELAY_BASE_URL}/v1/stream`
    recordPromptDispatch(body, endpoint)
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${jwt}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
      credentials: 'include',
      ...(signal ? { signal } : {}),
    })

    if (!res.ok) throw await buildContinuationHttpError(res)
    if (!res.body) {
      throw new ContinuationStreamError('[RelayAdapter] ReadableStream not supported in this environment.')
    }

    reader = res.body.getReader()
    onConnected?.()
    const decoder = new TextDecoder()
    const carry: SSECarry = { buffer: '' }

    while (true) {
      if (signal?.aborted) {
        await finishAbort()
        return abortHandle()
      }

      const { done, value } = await reader.read()
      if (signal?.aborted) {
        await finishAbort()
        return abortHandle()
      }
      if (done) {
        reachedEof = true
        throw new ContinuationProtocolError(
          '[RelayAdapter] Continuation stream ended without a valid done event.',
          { streamId: expectedStreamId },
        )
      }

      const chunk = decoder.decode(value, { stream: true })
      for (const event of parseSSE(chunk, carry)) {
        if (signal?.aborted) {
          await finishAbort()
          return abortHandle()
        }
        if (event.event === 'ping') continue
        if (event.event === 'token' || event.event === 'message') {
          if (event.data) await onChunk?.(event.data)
          continue
        }
        if (event.event === 'reasoning') {
          if (event.data) await onReasoningChunk?.(event.data)
          continue
        }
        if (event.event === 'tool_call_start') {
          let info: { tool_name?: string | null } | undefined
          if (event.data) {
            try { info = JSON.parse(event.data) } catch { /* progress metadata is optional */ }
          }
          await onToolCallStart?.(info)
          continue
        }
        if (event.event === 'tool_call') {
          throw new ContinuationProtocolError(
            '[RelayAdapter] Legacy tool_call event received during a continuation stream.',
            { streamId: expectedStreamId },
          )
        }
        if (event.event === 'error') {
          let error: ContinuationStreamError
          try {
            error = continuationErrorFromPayload(JSON.parse(event.data))
          } catch {
            error = new ContinuationStreamError(`[RelayAdapter] Stream error: ${event.data}`)
          }
          await finishError(error)
        }
        if (event.event === 'done') {
          let payload: unknown
          try {
            payload = JSON.parse(event.data)
          } catch {
            throw new ContinuationProtocolError(
              '[RelayAdapter] Continuation done payload is not valid JSON.',
              { streamId: expectedStreamId },
            )
          }
          const envelope = validateContinuationDoneEnvelope(payload, expectedStreamId)
          terminal = true
          await onComplete?.(false, envelope)
          return abortHandle()
        }
      }
    }
  } catch (error) {
    if (!terminal && ((error as Error)?.name === 'AbortError' || signal?.aborted)) {
      await finishAbort()
      return abortHandle()
    }
    return await finishError(error)
  } finally {
    if (reader) {
      if (!reachedEof) await reader.cancel().catch(() => undefined)
      reader.releaseLock()
    }
  }
}

/** Parse a `done` payload, tolerating a non-JSON body. */
function parseDoneMeta(data: string, label: string): StreamMeta | undefined {
  if (!data) return undefined;
  try {
    return JSON.parse(data);
  } catch {
    console.warn(`[RelayAdapter] ${label} failed to parse done payload as JSON:`, data);
    return undefined;
  }
}

/** `token`/`message`, `reasoning`, and `done` behave identically everywhere. */
function buildCommonStreamEvents({
  label,
  onChunk,
  onReasoningChunk,
  onComplete,
}: {
  label: string;
  onChunk?: ChunkCallback;
  onReasoningChunk?: (chunk: string) => void | Promise<void>;
  onComplete?: CompleteCallback;
}): Record<string, SSEEventHandler> {
  return {
    reasoning: async (data) => {
      // Reasoning deltas are ephemeral and never persisted.
      if (data) await onReasoningChunk?.(data);
    },
    // "token" is the relay's primary content event; "message" is a safe fallback.
    token: async (data) => {
      if (data) await onChunk?.(data);
    },
    message: async (data) => {
      if (data) await onChunk?.(data);
    },
    done: async (data): Promise<SSEEventOutcome> => {
      await onComplete?.(false, parseDoneMeta(data, label));
      return 'terminate';
    },
  };
}

/**
 * Surface a relay `error` event. `terminal` reflects the historical difference
 * between endpoints: conversation streams stop (the relay promises exactly one
 * terminal event), creator streams keep reading.
 */
function buildErrorEvent({
  onError,
  terminal,
}: {
  onError?: ErrorCallback;
  terminal: boolean;
}): SSEEventHandler {
  return async (data): Promise<SSEEventOutcome> => {
    try {
      const payload = JSON.parse(data);
      await onError?.(new Error(payload?.error || '[RelayAdapter] Unknown stream error'));
    } catch {
      await onError?.(new Error(`[RelayAdapter] Stream error: ${data}`));
    }
    return terminal ? 'terminate' : 'continue';
  };
}

/** Tool-call events (`tool_call_start`, `tool_call`). */
function buildToolEvents({
  onToolCall,
  onToolCallStart,
  onError,
}: {
  onToolCall?: CreatorToolCallCallback;
  onToolCallStart?: (info?: { tool_name?: string | null }) => void | Promise<void>;
  onError?: ErrorCallback;
}): Record<string, SSEEventHandler> {
  return {
    tool_call_start: async (data) => {
      let info: { tool_name?: string | null } | undefined;
      if (data) {
        try { info = JSON.parse(data); } catch { /* optional payload */ }
      }
      try {
        await onToolCallStart?.(info);
      } catch (error) {
        onError?.(error as Error);
      }
    },
    tool_call: async (data) => {
      if (!data) return;
      let payload: CreatorToolCallStreamEvent;
      try {
        payload = JSON.parse(data) as CreatorToolCallStreamEvent;
      } catch {
        onError?.(new Error(`[RelayAdapter] Invalid tool call payload: ${data}`));
        return;
      }

      try {
        await onToolCall?.(payload);
      } catch (error) {
        onError?.(error as Error);
      }
    },
  };
}

/** Serialize a stream request into the relay's request body. */
function buildStreamBody(request: GenerateStreamRequest): Record<string, unknown> {
  const body: Record<string, unknown> = {
    messages: request.messages,
    // Explicitly nullable: the relay accepts a null conversation_id.
    conversation_id: request.conversation_id ?? null,
  };

  const put = (key: string, value: unknown): void => {
    if (value !== undefined && value !== null) body[key] = value;
  };

  put('temperature', request.temperature);
  put('stream_id', request.stream_id);
  put('bot_id', request.bot_id);
  if (request.max_tokens !== undefined && request.max_tokens !== null) {
    body.max_tokens = request.max_tokens;
  }
  if (request.tools?.length) body.tools = request.tools;
  put('tool_choice', request.tool_choice);
  put('tool_protocol', request.tool_protocol);
  put('response_format', request.response_format);

  return body;
}

/**
 * The streaming entry point. Every surface reaches the same stateless relay
 * endpoint; whether an invocation carries tools is a property of the request,
 * not of a route.
 */
async function generateStream(
  request: GenerateStreamRequest,
  callbacks: GenerateStreamCallbacks = {},
): Promise<{ abort: () => void } | null> {
  const { onChunk, onReasoningChunk, onToolCall, onToolCallStart, onConnected, onComplete, onError, abortSignal } = callbacks;
  const label = 'generateStream';

  const body = buildStreamBody(request)
  if (request.tool_protocol === 'continuation_v1') {
    if (typeof request.stream_id !== 'string' || request.stream_id.length === 0) {
      const error = new ContinuationProtocolError(
        '[RelayAdapter] continuation_v1 requires an explicit stream_id.',
      )
      await onError?.(error)
      throw error
    }
    return runContinuationStream({ body, expectedStreamId: request.stream_id, callbacks })
  }

  return runSSEStream({
    path: '/v1/stream',
    body,
    label,
    abortSignal,
    recordDispatch: true,
    onConnected,
    onError,
    onComplete,
    events: {
      ...buildCommonStreamEvents({ label, onChunk, onReasoningChunk, onComplete }),
      // The relay promises exactly one terminal event and never follows `error`
      // with `done`.
      error: buildErrorEvent({ onError, terminal: true }),
      ...buildToolEvents({ onToolCall, onToolCallStart, onError }),
    },
  });
}

/**
 * Request a demo bot via the relay.
 */
async function requestDemoBot(): Promise<{ success: boolean; bot_id: number }> {
  const jwt = await getRelayAuthToken();

  const endpoint = `${RELAY_BASE_URL}/v1/openrouter/demo`;

  const res = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${jwt}`,
      'Content-Type': 'application/json',
    },
    body: '{}',
    credentials: 'include',
  });

  if (!res.ok) {
    const statusMessages: Record<number, string> = {
      401: 'Your session has expired. Please sign in again.',
      409: 'A demo bot already exists for your account.',
      502: 'Demo bot provisioning failed. Please try again later.',
    };
    let message = statusMessages[res.status] || `[RelayAdapter] HTTP ${res.status}`;
    const text = await res.text().catch(() => '');
    if (!statusMessages[res.status] && text) {
      try {
        const maybe = JSON.parse(text);
        if (maybe?.error) {
          message = maybe.error;
        }
      } catch {
        // ignore non-JSON errors
      }
    }
    const error: RelayError = new Error(message);
    error.status = res.status;
    throw error;
  }

  return res.json();
}

// Relay intentionally exports only relay-owned methods.
export const RelayAdapter = {
  generateStream,
  requestDemoBot,
};

export default RelayAdapter;
